export const MIN_PLAYER_COUNT = 3;
export const MAX_PLAYER_COUNT = 10;
export const DEFAULT_STARTING_GARNETS = 2;
export const HIDDEN_AUCTION_TRIGGER_ACCEPTED_COUNT = 17;
export const CUBE_VALUES = Array.from({ length: 33 }, (_, index) => 3 + index);

export type MinusAuctionPhase =
  | "AUCTION_DECISION"
  | "HIDDEN_AUCTION_BIDDING"
  | "HIDDEN_AUCTION_DECISION"
  | "GAME_OVER";

export type MinusAuctionStatus = "IN_PROGRESS" | "COMPLETED";
export type RejectResource = "CHIP" | "GARNET";
export type HiddenCubeDecision = "INCLUDE" | "EXCLUDE";

export interface ValidationResult {
  readonly valid: boolean;
  readonly error?: string;
  readonly details?: Record<string, unknown>;
}

export interface InitGameOptions {
  readonly playerIds: readonly string[];
  readonly hostPlayerId?: string;
  readonly gameId?: string;
  readonly seed?: number;
  readonly startingChips?: number;
  readonly startingGarnets?: number;
}

export interface MinusAuctionPlayer {
  readonly playerId: string;
  readonly chips: number;
  readonly garnets: number;
  readonly cubes: readonly number[];
}

export interface MinusAuctionViewerPlayer {
  readonly playerId: string;
  readonly chips: number | null;
  readonly garnets: number | null;
  readonly lockedGarnets: number | null;
  readonly availableGarnets: number | null;
  readonly cubes: readonly number[];
}

export interface AuctionRound {
  readonly currentCube: number;
  readonly pileChips: number;
  readonly pileGarnets: number;
  readonly startPlayerId: string;
  readonly currentPlayerId: string;
}

export interface HiddenAuctionState {
  readonly cube: number;
  readonly currentBid: number;
  readonly currentBidderId: string | null;
  readonly highestBidderId: string | null;
  readonly passedPlayerIds: readonly string[];
  readonly lastBidByPlayerId: Readonly<Record<string, number>>;
  readonly lockedGarnetsByPlayerId: Readonly<Record<string, number>>;
  readonly resumePlayerId: string;
}

export interface HiddenAuctionViewerState {
  readonly cube: number | null;
  readonly currentBid: number;
  readonly currentBidderId: string | null;
  readonly highestBidderId: string | null;
  readonly passedPlayerIds: readonly string[];
  readonly lastBidByPlayerId: Readonly<Record<string, number>>;
  readonly lockedGarnetsByPlayerId: Readonly<Record<string, number | null>>;
  readonly resumePlayerId: string;
}

export interface TransferLogEntry {
  readonly fromPlayerId: string;
  readonly toPlayerId: string;
  readonly amount: number;
  readonly phase: MinusAuctionPhase;
}

export interface AuctionLogEntry {
  readonly message: string;
  readonly playerId?: string;
  readonly cube?: number;
}

export interface ScoreEntry {
  readonly playerId: string;
  readonly scoredCubeSum: number;
  readonly remainingChips: number;
  readonly chipSurplus: number;
  readonly remainingGarnets: number;
  readonly finalScore: number;
  readonly scoredCubes: readonly number[];
  readonly eliminatedCubes: readonly number[];
}

export interface MinusAuctionState {
  readonly gameId: string;
  readonly status: MinusAuctionStatus;
  readonly phase: MinusAuctionPhase;
  readonly playerOrder: readonly string[];
  readonly players: readonly MinusAuctionPlayer[];
  readonly hostPlayerId: string;
  readonly seed: number;
  readonly deck: readonly number[];
  readonly removedHiddenCube: number;
  readonly secondHiddenCube: number | null;
  readonly secondHiddenCubeIncluded: boolean;
  readonly hiddenAuctionResolved: boolean;
  readonly acceptedCubeCount: number;
  readonly auction: AuctionRound | null;
  readonly hiddenAuction: HiddenAuctionState | null;
  readonly transferLog: readonly TransferLogEntry[];
  readonly auctionLog: readonly AuctionLogEntry[];
  readonly scores: readonly ScoreEntry[];
  readonly winnerIds: readonly string[];
  readonly eliminationCandidateIds: readonly string[];
}

export interface MinusAuctionViewerState {
  readonly gameId: string;
  readonly status: MinusAuctionStatus;
  readonly phase: MinusAuctionPhase;
  readonly playerOrder: readonly string[];
  readonly players: readonly MinusAuctionViewerPlayer[];
  readonly hostPlayerId: string;
  readonly viewerPlayerId: string | null;
  readonly viewerRole: "player" | "observer";
  readonly secondHiddenCubeIncluded: boolean;
  readonly hiddenAuctionResolved: boolean;
  readonly acceptedCubeCount: number;
  readonly auction: AuctionRound | null;
  readonly hiddenAuction: HiddenAuctionViewerState | null;
  readonly transferLog: readonly TransferLogEntry[];
  readonly auctionLog: readonly AuctionLogEntry[];
  readonly scores: readonly ScoreEntry[];
  readonly winnerIds: readonly string[];
  readonly eliminationCandidateIds: readonly string[];
  readonly redactedFields: readonly string[];
}

export interface RejectCubeAction {
  readonly type: "RejectCube";
  readonly playerId: string;
  readonly resource: RejectResource;
}

export interface TakeCubeAction {
  readonly type: "TakeCube";
  readonly playerId: string;
}

export interface TransferGarnetAction {
  readonly type: "TransferGarnet";
  readonly fromPlayerId: string;
  readonly toPlayerId: string;
  readonly amount: number;
}

export interface BidHiddenCubeAction {
  readonly type: "BidHiddenCube";
  readonly playerId: string;
  readonly amount: number;
}

export interface PassHiddenCubeBidAction {
  readonly type: "PassHiddenCubeBid";
  readonly playerId: string;
}

export interface DecideHiddenCubeAction {
  readonly type: "DecideHiddenCube";
  readonly playerId: string;
  readonly decision: HiddenCubeDecision;
}

export type MinusAuctionAction =
  | RejectCubeAction
  | TakeCubeAction
  | TransferGarnetAction
  | BidHiddenCubeAction
  | PassHiddenCubeBidAction
  | DecideHiddenCubeAction;
